;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import {
	type FiltersByPath,
	readExplicitUrlFilters,
	resolveFiltersForPath,
	syncFiltersByPathEntry,
	type TopLevelFilterMap,
} from "@/lib/stores/top-level-filter-path";
import { createUrlStorage, TOP_LEVEL_FILTERS_URL_PARAM } from "./url-storage";

// =============================================================================
// Types
// =============================================================================

export interface SwitchPathOptions {
	/** Reconcile filters after URL rehydration, even when pathname is unchanged. */
	force?: boolean;
}

interface TopLevelFilterState {
	_hasHydrated: boolean;
	currentPath: string | null;
	filters: TopLevelFilterMap;
	filtersByPath: FiltersByPath;
}

interface TopLevelFilterActions {
	setFilter: (key: string, value: unknown) => void;
	setFilters: (filters: TopLevelFilterMap) => void;
	removeFilter: (key: string) => void;
	clearFilters: () => void;
	setHasHydrated: (hydrated: boolean) => void;
	switchPath: (pathname: string, options?: SwitchPathOptions) => void;
}

type TopLevelFilterStore = TopLevelFilterState & TopLevelFilterActions;

// =============================================================================
// Store
// =============================================================================

const DEBOUNCE_MS = 300;

function applyFilterMutation(
	state: TopLevelFilterState,
	nextFilters: TopLevelFilterMap,
): TopLevelFilterState {
	return {
		...state,
		filters: nextFilters,
		filtersByPath: syncFiltersByPathEntry({
			filters: nextFilters,
			filtersByPath: state.filtersByPath,
			pathname: state.currentPath,
		}),
	};
}

function reconcileCurrentPathFilters(
	state: TopLevelFilterState,
	pathname: string,
): TopLevelFilterState {
	const filters = readExplicitUrlFilters() ?? { ...state.filters };

	return {
		...state,
		currentPath: pathname,
		filters,
		filtersByPath: syncFiltersByPathEntry({
			filters,
			filtersByPath: state.filtersByPath,
			pathname,
		}),
	};
}

export const useTopLevelFilterStore = create<TopLevelFilterStore>()(
	devtools(
		persist(
			(set, get) => ({
				_hasHydrated: false,
				clearFilters: () => {
					set((state) => applyFilterMutation(state, {}), false, "clearFilters");
				},
				currentPath: null,
				filters: {},
				filtersByPath: {},

				removeFilter: (key) => {
					set(
						(state) => {
							const { [key]: _removed, ...rest } = state.filters;
							return applyFilterMutation(state, rest);
						},
						false,
						"removeFilter",
					);
				},

				setFilter: (key, value) => {
					set(
						(state) =>
							applyFilterMutation(state, {
								...state.filters,
								[key]: value,
							}),
						false,
						"setFilter",
					);
				},

				setFilters: (filters) => {
					set(
						(state) => applyFilterMutation(state, filters),
						false,
						"setFilters",
					);
				},

				setHasHydrated: (hydrated) => {
					set({ _hasHydrated: hydrated }, false, "setHasHydrated");
				},

				switchPath: (pathname, options) => {
					const state = get();

					if (options?.force && state.currentPath === pathname) {
						set(
							reconcileCurrentPathFilters(state, pathname),
							false,
							"switchPath/reconcile",
						);
						return;
					}

					if (state.currentPath === pathname) {
						return;
					}

					const { filters, filtersByPath } = resolveFiltersForPath({
						filtersByPath: state.filtersByPath,
						pathname,
						previousFilters: state.filters,
						previousPath: state.currentPath,
					});

					set(
						{
							currentPath: pathname,
							filters,
							filtersByPath,
						},
						false,
						"switchPath",
					);
				},
			}),
			{
				name: "top-level-filters",
				partialize: (state) => ({ filters: state.filters }),
				storage: createJSONStorage(() =>
					createUrlStorage({
						debounceMs: DEBOUNCE_MS,
						paramName: TOP_LEVEL_FILTERS_URL_PARAM,
					}),
				),
			},
		),
		{ name: "top-level-filter-store" },
	),
);

// =============================================================================
// Selectors (for optimized re-renders)
// =============================================================================

export const selectFilters = (state: TopLevelFilterStore) => state.filters;
export const selectFilter = (key: string) => (state: TopLevelFilterStore) =>
	state.filters[key];
